
import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import OnboardingLayout from '../../OnboardingLayout';
import ProfilePictureUpload from '@/components/profile/ProfilePictureUpload';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters long" }),
  bio: z.string().min(50, { message: "Bio should be at least 50 characters to be compelling" }).max(300, { message: "Bio should not exceed 300 characters" }),
  location: z.string().min(2, { message: "Please add your location" }),
  headline: z.string().min(5, { message: "Add a short headline that describes your work" }).max(100, { message: "Headline should not exceed 100 characters" }),
});

interface ProfileCreationProps {
  formData: any;
  updateFormData: (data: any) => void;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
}

const ProfileCreation: React.FC<ProfileCreationProps> = ({
  formData,
  updateFormData,
  currentStep,
  totalSteps,
  onNext,
  onPrevious
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: formData.name || '',
      bio: formData.bio || '',
      location: formData.location || '',
      headline: formData.headline || '',
    },
  });

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    updateFormData(values);
    onNext();
  };
  
  const handleAvatarUpdate = async (url: string) => {
    if (!user) return;
    
    try {
      // Update form data with the avatar URL
      updateFormData({
        ...formData,
        avatar_url: url
      });
      
      // Optionally update the profile in the database right away
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: url })
        .eq('id', user.id);
      
      if (error) throw error;
      
      toast({
        title: 'Profile picture updated',
        description: 'Your profile picture has been successfully updated.',
      });
    } catch (error) {
      console.error('Error updating profile picture:', error);
      toast({
        variant: 'destructive',
        title: 'Update failed',
        description: 'There was a problem updating your profile picture.',
      });
    }
  };

  return (
    <OnboardingLayout
      title="Create Your Profile"
      subtitle="Tell readers about yourself and your journalism background"
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={form.handleSubmit(handleSubmit)}
      onPrevious={onPrevious}
      nextDisabled={!form.formState.isValid}
    >
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-center mb-8">
          <ProfilePictureUpload
            avatarUrl={formData.avatar_url}
            onUploadComplete={handleAvatarUpdate}
            size="lg"
          />
        </div>
        
        <Form {...form}>
          <form className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Jane Smith" {...field} />
                  </FormControl>
                  <FormDescription>
                    This is how your name will appear to readers.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="headline"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Professional Headline</FormLabel>
                  <FormControl>
                    <Input placeholder="Investigative Journalist | Climate Specialist | Pulitzer Winner" {...field} />
                  </FormControl>
                  <FormDescription>
                    A brief headline that summarizes your expertise (will appear under your name).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location</FormLabel>
                  <FormControl>
                    <Input placeholder="New York, NY" {...field} />
                  </FormControl>
                  <FormDescription>
                    Your general location helps readers connect with you.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="bio"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bio</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="I'm a journalist with 10 years of experience covering climate change and environmental policy. Previously a staff writer at The Washington Post, I've won multiple awards for my reporting on..."
                      {...field}
                      className="min-h-32 resize-y"
                    />
                  </FormControl>
                  <FormDescription>
                    <div className="flex justify-between">
                      <span>Write a compelling bio that highlights your experience and expertise.</span>
                      <span className={`${field.value.length < 50 || field.value.length > 300 ? 'text-destructive' : ''}`}>
                        {field.value.length}/300
                      </span>
                    </div>
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
        
        <div className="bg-muted/30 rounded-lg p-4 mt-8">
          <h4 className="text-sm font-medium mb-2">Tips for a compelling bio:</h4>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li>• Include your journalistic background and experience</li>
            <li>• Mention notable publications you've worked with</li>
            <li>• Highlight any awards or recognition you've received</li>
            <li>• Share your areas of expertise and what topics you cover</li>
            <li>• Add a personal touch to help readers connect with you</li>
          </ul>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default ProfileCreation;
